import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    _paq?: unknown[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class MatomoService {
  readonly CATEGORY = {
    app: 'app',
    export: 'export',
    share: 'share',
    space: 'space'
  } as const;

  readonly ACTION = {
    view: 'view',
    createSuccess: 'create_success',
    deleteFailed: 'delete_failed',
    deleteSuccess: 'delete_success',
    emailShareFailed: 'email_share_failed',
    emailShareTriggered: 'email_share_triggered',
    importFailed: 'import_failed',
    importSuccess: 'import_success',
    pdfExportFailed: 'pdf_export_failed',
    pdfExportSuccess: 'pdf_export_success',
    copyLinkFailed: 'copy_link_failed',
    copyLinkSuccess: 'copy_link_success'
  } as const;

  private initialized = false;
  private appViewTracked = false;
  private readonly appName = 'amplify';

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  init(): void {
    if (this.initialized || !this.isBrowser() || !this.isConfigured()) {
      return;
    }

    const paq = this.getPaqQueue();
    const baseUrl = this.getNormalizedBaseUrl();

    paq.push(
      ['setTrackerUrl', `${baseUrl}/matomo.php`],
      ['setSiteId', environment.matomoSiteId]
    );

    this.injectScript(baseUrl);
    this.initialized = true;
  }

  trackAppView(): void {
    if (this.appViewTracked) {
      return;
    }

    const tracked = this.trackActionPageView(this.CATEGORY.app, this.ACTION.view, 'app_shell_loaded');
    this.appViewTracked = tracked;
  }

  trackActionPageView(category: string, action: string, context?: string): boolean {
    this.init();
    if (!this.initialized) {
      return false;
    }

    const paq = this.getPaqQueue();
    const customUrl = this.buildPagePath(category, action, context);
    const documentTitle = this.buildPageTitle(category, action, context);

    paq.push(
      ['setCustomUrl', customUrl],
      ['setDocumentTitle', documentTitle],
      ['trackPageView']
    );

    return true;
  }

  // Keep event tracking as optional fallback for future use.
  trackEvent(category: string, action: string, name?: string, value?: number): void {
    this.init();
    if (!this.initialized) {
      return;
    }

    const paq = this.getPaqQueue();
    const payload: unknown[] = ['trackEvent', category, action];

    if (name !== undefined) {
      payload.push(name);
    }

    if (value !== undefined) {
      if (name === undefined) {
        payload.push('');
      }
      payload.push(value);
    }

    paq.push(payload);
  }

  private buildPagePath(category: string, action: string, context?: string): string {
    const origin = this.document.location?.origin ?? '';
    const segments = [this.appName, category, action, context].filter(Boolean);
    return `${origin}/${segments.join('/')}`;
  }

  private buildPageTitle(category: string, action: string, context?: string): string {
    const segments = [this.appName, category, action, context].filter(Boolean);
    return segments.join('/');
  }

  private getPaqQueue(): unknown[] {
    const targetWindow = globalThis as unknown as Window;
    targetWindow._paq = targetWindow._paq ?? [];
    return targetWindow._paq;
  }

  private injectScript(baseUrl: string): void {
    const existingScript = this.document.querySelector('script[data-matomo-script="true"]');
    if (existingScript) {
      return;
    }

    const script = this.document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `${baseUrl}/matomo.js`;
    script.dataset['matomoScript'] = 'true';
    this.document.head.appendChild(script);
  }

  private isConfigured(): boolean {
    return Boolean(environment.matomoEnabled && environment.matomoUrl && environment.matomoSiteId);
  }

  private getNormalizedBaseUrl(): string {
    return environment.matomoUrl.trim().replace(/\/$/, '');
  }

  private isBrowser(): boolean {
    return globalThis.window !== undefined && this.document.defaultView !== null;
  }
}
